import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import { DefaultResponseWithData } from "../models/system";
import apiConfig from "../utils/apiConfig";

/** Mirrors GPMetals.Core.Models.Lead.LeadEmailImportOutcome. */
export type LeadEmailImportOutcome =
  | "Imported"
  | "WouldImport"
  | "DuplicateOffer"
  | "DuplicateRecentVrm"
  | "NoRegistration"
  | "ParseFailed"
  | "UnknownSubject"
  | "ReplyOrForward"
  | "Error";

export interface LeadEmailImportItem {
  messageId: string;
  subject: string | null;
  from: string | null;
  received: string | null;
  wasUnread: boolean;
  /** Partner the subject routed to (SMCO, SCC, CarCo...), null when nothing matched. */
  source: string | null;
  vrm: string | null;
  make: string | null;
  model: string | null;
  postcode: string | null;
  price: number | null;
  /** The partner's own reference, e.g. SMCO-123456. */
  ccid: string | null;
  outcome: LeadEmailImportOutcome;
  reason: string | null;
  /** For the duplicate outcomes: the lead this one collided with. */
  existingCcid: string | null;
  createdLeadId: string | null;
  createdLeadCcid: string | null;
  /** Folder the message was filed into. Null on a dry run, which moves nothing. */
  movedToFolder: string | null;
}

export interface LeadEmailMailboxSnapshot {
  configured: boolean;
  address: string | null;
  unreadTotal: number;
  unreadMatchingSubjects: number;
  /** Unread mail the importer's subject filter ignores - what a template change looks like. */
  unreadNotMatchingSubjects: number;
  unmatchedSubjects: string[];
  matchedSubjects: string[];
  /** Set when the mailbox could not be read at all - bad credentials, rejected query. */
  error: string | null;
}

export interface LeadEmailImportReport {
  dryRun: boolean;
  startedUtc: string;
  durationMs: number;
  examined: number;
  imported: number;
  duplicates: number;
  failed: number;
  skipped: number;
  items: LeadEmailImportItem[];
  mailbox: LeadEmailMailboxSnapshot;
  message: string | null;
}

export interface LeadEmailImportProbeResult {
  vrm: string | null;
  found: boolean;
  item: LeadEmailImportItem | null;
  /** Other inbox mail for the same car - reported, never processed. */
  otherMatches: LeadEmailImportItem[];
  mailbox: LeadEmailMailboxSnapshot;
  message: string | null;
}

export default function useLeadImport(componentName = "useLeadImport") {
  // Email import can run past any fixed client timeout; CSV import of a large
  // Remove My Car / FADS file can too. Run both uncapped (timeoutMs: 0).
  const { get, post } = useApiHelper({ ...apiConfig(componentName), timeoutMs: 0 });
  const queryClient = useQueryClient();

  const mailbox = useQuery(
    ["lead-import", "mailbox"],
    () => get<DefaultResponseWithData<LeadEmailMailboxSnapshot>>("/lead-import/mailbox"),
    { refetchOnWindowFocus: false }
  );

  const probe = useMutation(({ vrm, vin, dryRun }: { vrm?: string; vin?: string; dryRun: boolean }) =>
    post<{ vrm?: string; vin?: string; dryRun: boolean }, DefaultResponseWithData<LeadEmailImportProbeResult>>(
      "/lead-import/probe",
      { vrm, vin, dryRun }
    )
  );

  const run = useMutation(({ dryRun }: { dryRun: boolean }) =>
    post<{ dryRun: boolean }, DefaultResponseWithData<LeadEmailImportReport>>(
      "/lead-import/run",
      { dryRun }
    )
  );

  const exportLeads = useMutation((leadIds: { leads: string[] }) => post("/lead/exported", leadIds), {
    onSuccess: () => {
      queryClient.invalidateQueries(["leads"]);
    },
  });

  const importLeads = useMutation((leads: { leads: unknown[] }) => post("/lead/import", leads), {
    onSuccess: () => {
      queryClient.invalidateQueries(["leads"]);
      queryClient.invalidateQueries(["pagedTable", "/lead/partialwithfilters"]);
    },
  });

  return { mailbox, probe, run, exportLeads, importLeads };
}
